import { entries, isEmpty, isNil, last } from "lodash";
import { Row, RowType } from "PFApp/booking/types/row";
import { useMemo } from "react";

import { useBookingRowsContext } from "../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";

type UseParentsAndChildrenMode = {
  row: Pick<Row, "key" | "parents" | "type">;
  childrenCount?: number;
};

type UseParentsAndChildrenModeReturn = {
  isAllBookingsModeEnabled: boolean;
  isModeForAllChildrenEnabled?: boolean;
};

export const useParentsAndChildrenMode = ({
  row: { key, parents = [], type },
  childrenCount
}: UseParentsAndChildrenMode): UseParentsAndChildrenModeReturn => {
  const { calendarMode } = useBookingsOverviewContext();
  const { allBookingsModeRowsState, rowsByCalendarMode } = useBookingRowsContext();

  const rows = rowsByCalendarMode[calendarMode];
  const isAllBookingsModeEnabledForRow = !!allBookingsModeRowsState[key];

  const isModeForParentRowEnabled = useMemo(() => {
    const closestParent = last(parents);
    return !!closestParent && !!allBookingsModeRowsState[closestParent];
  }, [allBookingsModeRowsState, parents]);

  const isModeForAllChildrenEnabled = useMemo(() => {
    if (!childrenCount) {
      return undefined;
    }
    const childrenActivityRows = rows
      .filter(({ parents: rowParents }) => !!rowParents?.some((parentKey) => parentKey === key))
      .filter(({ type }) => [RowType.Engagement, RowType.Role].includes(type));
    const enabledChildrenCount = entries(allBookingsModeRowsState).filter(
      ([rowKey, isEnabled]) => rowKey !== key && rowKey.includes(key) && isEnabled
    ).length;
    return !isEmpty(childrenActivityRows) && enabledChildrenCount === childrenCount;
  }, [rows, key, allBookingsModeRowsState, childrenCount]);

  const isAllBookingsModeEnabled =
    type === RowType.Profile ? isModeForParentRowEnabled : isAllBookingsModeEnabledForRow;

  return {
    isAllBookingsModeEnabled,
    ...(!isNil(childrenCount) ? { isModeForAllChildrenEnabled } : {})
  };
};
